import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Linkedin, Twitter, Github, Globe, Mail, Briefcase, MapPin, 
  Calendar, Award, Star, Users, TrendingUp, Heart, Languages,
  ExternalLink, ArrowLeft, Share2, Check, Loader2
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PublicProfilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchPublicProfile();
  }, [userId]);

  const fetchPublicProfile = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/user/profile/${userId}/public`);
      const data = await response.json();
      
      if (data.success) {
        setProfile(data.profile);
      } else {
        toast({
          title: 'Error',
          description: 'Profile not found',
          variant: 'destructive'
        });
        navigate('/');
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast({
        title: 'Error',
        description: 'Failed to load profile',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleShare = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast({
      title: 'Link Copied!',
      description: 'Profile URL copied to clipboard'
    });
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return null;
  }

  const getSocialIcon = (platform) => {
    const icons = {
      twitter: Twitter,
      github: Github,
      portfolio: Globe,
      linkedin: Linkedin
    };
    return icons[platform] || Globe;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
        }}></div>
        
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="flex items-start justify-between mb-6">
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              size="sm"
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
            
            <Button
              onClick={handleShare}
              size="sm"
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
            >
              {copied ? <Check className="w-4 h-4 mr-2" /> : <Share2 className="w-4 h-4 mr-2" />}
              {copied ? 'Copied!' : 'Share Profile'}
            </Button>
          </div>

          <div className="flex flex-col md:flex-row items-center gap-8">
            {/* Profile Picture */}
            <div className="relative">
              <div className="w-40 h-40 rounded-full overflow-hidden border-4 border-white shadow-2xl bg-white">
                {profile.profile_picture ? (
                  <img 
                    src={profile.profile_picture} 
                    alt={profile.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-500">
                    <span className="text-6xl font-bold text-white">
                      {profile.name?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
              {profile.featured_stat && (
                <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 bg-yellow-400 text-yellow-900 px-4 py-1 rounded-full text-xs font-bold shadow-lg whitespace-nowrap">
                  <Star className="w-3 h-3 inline mr-1" />
                  {profile.featured_stat.label}
                </div>
              )}
            </div>

            {/* Profile Info */}
            <div className="flex-1 text-center md:text-left">
              <h1 className="text-5xl font-bold mb-2">{profile.name}</h1>
              {profile.recruiter_score && (
                profile.recruiter_score.score >= 40 ? (
                  <div data-testid="recruiter-score-badge" className="inline-flex items-center gap-2 bg-white/15 border border-white/30 rounded-full px-4 py-1.5 mb-3">
                    <span className="text-yellow-300 font-bold text-lg">★</span>
                    <span className="font-semibold">Recruiter Score: {profile.recruiter_score.score}/100{profile.recruiter_score.level ? ` · ${profile.recruiter_score.level}` : ''}</span>
                  </div>
                ) : (
                  <div data-testid="recruiter-score-badge" className="inline-flex items-center gap-2 bg-white/15 border border-white/30 rounded-full px-4 py-1.5 mb-3">
                    <span className="text-yellow-300 font-bold text-lg">⭐</span>
                    <span className="font-semibold">Rising Recruiter</span>
                  </div>
                )
              )}
              {profile.job_title && (
                <p className="text-2xl text-blue-100 mb-2">{profile.job_title}</p>
              )}
              {profile.tagline && (
                <p className="text-lg text-white/90 mb-4 italic">"{profile.tagline}"</p>
              )}
              
              <div className="flex flex-wrap gap-4 justify-center md:justify-start mb-4">
                {profile.current_company && (
                  <div className="flex items-center gap-2 text-white/90">
                    <Briefcase className="w-4 h-4" />
                    <span>{profile.current_company}</span>
                  </div>
                )}
                {profile.location && (
                  <div className="flex items-center gap-2 text-white/90">
                    <MapPin className="w-4 h-4" />
                    <span>{profile.location}</span>
                  </div>
                )}
                {profile.total_experience && (
                  <div className="flex items-center gap-2 text-white/90">
                    <Calendar className="w-4 h-4" />
                    <span>{profile.total_experience}+ years experience</span>
                  </div>
                )}
              </div>

              {/* Social Links */}
              <div className="flex gap-3 justify-center md:justify-start">
                {profile.linkedin_url && (
                  <a 
                    href={profile.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-all"
                  >
                    <Linkedin className="w-5 h-5" />
                  </a>
                )}
                {Object.entries(profile.social_links || {}).map(([platform, url]) => {
                  if (!url) return null;
                  const Icon = getSocialIcon(platform);
                  return (
                    <a 
                      key={platform}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-all"
                      title={platform}
                    >
                      <Icon className="w-5 h-5" />
                    </a>
                  );
                })}
              </div>
            </div>

            {/* Feature Stat Card */}
            {profile.featured_stat && (
              <Card className="bg-white/10 backdrop-blur-sm border-white/20 text-white min-w-[200px]">
                <CardContent className="pt-6 text-center">
                  <TrendingUp className="w-8 h-8 mx-auto mb-2" />
                  <div className="text-4xl font-bold mb-1">{profile.featured_stat.label}</div>
                  <p className="text-sm text-white/80">{profile.featured_stat.description}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 py-12 -mt-8 relative z-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* About */}
            {profile.bio && (
              <Card className="shadow-lg border-2 border-blue-100">
                <CardContent className="pt-6">
                  <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Users className="w-6 h-6 text-blue-600" />
                    About Me
                  </h2>
                  <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{profile.bio}</p>
                </CardContent>
              </Card>
            )}

            {/* Top 3 Active Jobs */}
            {profile.active_jobs && profile.active_jobs.length > 0 && (
              <Card className="shadow-lg border-2 border-purple-100">
                <CardContent className="pt-6">
                  <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Briefcase className="w-6 h-6 text-purple-600" />
                    Current Openings
                  </h2>
                  <div className="space-y-4">
                    {profile.active_jobs.map((job) => (
                      <div 
                        key={job.job_id}
                        className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200 hover:shadow-md transition-all cursor-pointer"
                        onClick={() => navigate(`/jobs/${job.job_id}`)}
                      >
                        <h3 className="font-bold text-lg text-slate-900 mb-2">{job.title}</h3>
                        <div className="flex flex-wrap gap-3 text-sm text-slate-600">
                          <span className="flex items-center gap-1">
                            <Briefcase className="w-4 h-4" />
                            {job.company_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {job.location}
                          </span>
                          <Badge className="bg-purple-600">{job.job_type?.replace('_', ' ')}</Badge>
                        </div>
                        <Button 
                          size="sm" 
                          className="mt-3 bg-purple-600 hover:bg-purple-700"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/jobs/${job.job_id}`);
                          }}
                        >
                          View Job <ExternalLink className="w-3 h-3 ml-2" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Experience */}
            {profile.experience && profile.experience.length > 0 && (
              <Card className="shadow-lg border-2 border-green-100">
                <CardContent className="pt-6">
                  <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Briefcase className="w-6 h-6 text-green-600" />
                    Experience
                  </h2>
                  <div className="space-y-6">
                    {profile.experience.map((exp, idx) => (
                      <div key={idx} className="relative pl-8 pb-6 border-l-2 border-green-300 last:border-l-0 last:pb-0">
                        <div className="absolute left-0 top-0 w-4 h-4 bg-green-500 rounded-full -translate-x-[9px]"></div>
                        <h3 className="font-bold text-lg text-slate-900">{exp.title}</h3>
                        <p className="text-green-600 font-semibold">{exp.company}</p>
                        <p className="text-sm text-slate-600 mb-2">
                          {exp.start_date} - {exp.end_date || 'Present'}
                          {exp.location && ` • ${exp.location}`}
                        </p>
                        {exp.description && (
                          <p className="text-slate-700 text-sm">{exp.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Testimonials */}
            {profile.testimonials && profile.testimonials.length > 0 && (
              <Card className="shadow-lg border-2 border-yellow-100 bg-gradient-to-br from-yellow-50 to-orange-50">
                <CardContent className="pt-6">
                  <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Heart className="w-6 h-6 text-red-500" />
                    What Others Say
                  </h2>
                  <div className="space-y-4">
                    {profile.testimonials.map((testimonial, idx) => (
                      <div key={idx} className="p-4 bg-white rounded-lg border-l-4 border-yellow-400">
                        <p className="text-slate-700 italic mb-3">"{testimonial.text}"</p>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-orange-400 rounded-full flex items-center justify-center">
                            <span className="text-white font-bold text-sm">
                              {testimonial.name?.charAt(0)?.toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900">{testimonial.name}</p>
                            {testimonial.title && testimonial.company && (
                              <p className="text-xs text-slate-600">{testimonial.title} at {testimonial.company}</p>
                            )}
                          </div>
                          {testimonial.linkedin_url && (
                            <a 
                              href={testimonial.linkedin_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ml-auto text-blue-600 hover:text-blue-700"
                            >
                              <Linkedin className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Skills */}
            {profile.skills && profile.skills.length > 0 && (
              <Card className="shadow-lg border-2 border-blue-100">
                <CardContent className="pt-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Star className="w-5 h-5 text-blue-600" />
                    Skills
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {profile.skills.map((skill, idx) => (
                      <Badge 
                        key={idx}
                        className="bg-blue-100 text-blue-700 hover:bg-blue-200 border-blue-300"
                      >
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Languages */}
            {profile.languages && profile.languages.length > 0 && (
              <Card className="shadow-lg border-2 border-purple-100">
                <CardContent className="pt-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Languages className="w-5 h-5 text-purple-600" />
                    Languages
                  </h3>
                  <div className="space-y-3">
                    {profile.languages.map((lang, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <span className="text-slate-900 font-medium">{lang.language}</span>
                        <Badge className="bg-purple-100 text-purple-700 border-purple-300">
                          {lang.proficiency}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Certifications */}
            {profile.certifications && profile.certifications.length > 0 && (
              <Card className="shadow-lg border-2 border-green-100">
                <CardContent className="pt-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Award className="w-5 h-5 text-green-600" />
                    Certifications
                  </h3>
                  <div className="space-y-3">
                    {profile.certifications.map((cert, idx) => (
                      <div key={idx} className="p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                        <div className="flex items-start gap-2">
                          <Award className="w-5 h-5 text-green-600 mt-0.5" />
                          <div>
                            <p className="font-semibold text-slate-900">{cert.certification_name}</p>
                            <p className="text-xs text-slate-600">Level: {cert.level}</p>
                            {cert.score && (
                              <Badge className="bg-green-600 text-white mt-1 text-xs">
                                Score: {cert.score}%
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Achievements */}
            {profile.achievements && profile.achievements.length > 0 && (
              <Card className="shadow-lg border-2 border-yellow-100">
                <CardContent className="pt-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Award className="w-5 h-5 text-yellow-600" />
                    Achievements
                  </h3>
                  <ul className="space-y-2">
                    {profile.achievements.map((achievement, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-slate-700">
                        <span className="text-yellow-500 mt-1">★</span>
                        <span className="text-sm">{achievement}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Hobbies */}
            {profile.hobbies && profile.hobbies.length > 0 && (
              <Card className="shadow-lg border-2 border-pink-100">
                <CardContent className="pt-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Heart className="w-5 h-5 text-pink-600" />
                    Hobbies & Interests
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {profile.hobbies.map((hobby, idx) => (
                      <Badge 
                        key={idx}
                        className="bg-pink-100 text-pink-700 hover:bg-pink-200 border-pink-300"
                      >
                        {hobby}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Footer CTA */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-12">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">Interested in working with {profile.name?.split(' ')[0]}?</h2>
          <p className="text-xl mb-6 text-white/90">Connect on LinkedIn or explore current job openings</p>
          <div className="flex gap-4 justify-center">
            {profile.linkedin_url && (
              <Button
                onClick={() => window.open(profile.linkedin_url, '_blank')}
                size="lg"
                className="bg-white text-blue-600 hover:bg-blue-50"
              >
                <Linkedin className="w-5 h-5 mr-2" />
                Connect on LinkedIn
              </Button>
            )}
            {profile.active_jobs && profile.active_jobs.length > 0 && (
              <Button
                onClick={() => navigate('/jobs')}
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white/10"
              >
                <Briefcase className="w-5 h-5 mr-2" />
                View Open Positions
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicProfilePage;
